import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Sparkles, 
  ArrowRight, 
  ArrowLeft, 
  Building2, 
  Briefcase, 
  Target, 
  Users, 
  Loader2,
  CheckCircle2,
  Mail,
  Plus,
  X
} from "lucide-react";
import { toast } from "sonner";
import { useInviteTeamMember } from "@/hooks/useTeam";
import { z } from "zod";

type Step = 1 | 2 | 3 | 4;

const emailSchema = z.string().email("Please enter a valid email");

const teamSizeOptions = [
  { value: "1", label: "Just me" },
  { value: "2-10", label: "2-10 people" },
  { value: "11-50", label: "11-50 people" },
  { value: "51+", label: "51+ people" },
];

const goalOptions = [
  { value: "lead-management", label: "Better lead management", icon: Target },
  { value: "sales-automation", label: "Automate sales tasks", icon: Sparkles },
  { value: "team-collaboration", label: "Improve team collaboration", icon: Users },
  { value: "analytics", label: "Get better analytics", icon: Building2 },
];

export default function Onboarding() {
  const [step, setStep] = useState<Step>(1);
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [goals, setGoals] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const inviteMember = useInviteTeamMember();

  const handleGoalToggle = (goal: string) => {
    setGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const handleAddEmail = () => {
    setEmailError("");
    const result = emailSchema.safeParse(currentEmail);
    if (!result.success) {
      setEmailError("Please enter a valid email");
      return;
    }
    if (inviteEmails.includes(currentEmail)) {
      setEmailError("Email already added");
      return;
    }
    setInviteEmails(prev => [...prev, currentEmail]);
    setCurrentEmail("");
  };

  const handleRemoveEmail = (email: string) => {
    setInviteEmails(prev => prev.filter(e => e !== email));
  };

  const handleNext = () => {
    if (step === 1 && !companyName.trim()) {
      toast.error("Please enter your company name");
      return;
    }
    if (step === 2 && !teamSize) {
      toast.error("Please select your team size");
      return;
    }
    if (step === 3 && goals.length === 0) {
      toast.error("Please select at least one goal");
      return;
    }
    setStep(prev => (prev + 1) as Step);
  };

  const handleBack = () => {
    setStep(prev => (prev - 1) as Step);
  };

  const handleComplete = async () => {
    setIsLoading(true);
    
    try {
      // Update profile
      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: companyName,
          job_title: jobTitle,
          onboarding_completed: true,
        })
        .eq("id", user?.id);

      if (error) throw error;

      // Send invitations
      for (const email of inviteEmails) {
        await inviteMember.mutateAsync({ email, role: "sales_agent" });
      }

      toast.success("Welcome to Aiveliq360! Let's get started.");
      navigate("/app");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const firstName = profile?.first_name || "there";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary ai-glow">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </div>
          <span className="font-bold text-xl">
            Aiveliq<span className="text-primary">360</span>
          </span>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all duration-300 ${
                s === step 
                  ? "w-8 bg-primary" 
                  : s < step 
                    ? "w-2 bg-primary/60" 
                    : "w-2 bg-muted"
              }`}
            />
          ))}
        </div>

        <div className="glass rounded-2xl p-8">
          {/* Step 1: Company & Role */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-2">
                  Hey {firstName}! 👋
                </h1>
                <p className="text-muted-foreground">
                  Let's personalize your experience
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Company name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company"
                      placeholder="Acme Corp"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Your role (optional)</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="jobTitle"
                      placeholder="Sales Manager"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Team Size */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-2">
                  How big is your team?
                </h1>
                <p className="text-muted-foreground">
                  This helps us tailor the experience
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {teamSizeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTeamSize(option.value)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      teamSize === option.value
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <Users className={`h-5 w-5 mb-2 ${
                      teamSize === option.value ? "text-primary" : "text-muted-foreground"
                    }`} />
                    <p className="font-medium">{option.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Goals */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-2">
                  What are your main goals?
                </h1>
                <p className="text-muted-foreground">
                  Select all that apply
                </p>
              </div>

              <div className="space-y-3">
                {goalOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = goals.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleGoalToggle(option.value)}
                      className={`w-full p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                        isSelected
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-medium flex-1 text-left">{option.label}</span>
                      {isSelected && (
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Invite Team */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-2">
                  Invite your team
                </h1>
                <p className="text-muted-foreground">
                  Add colleagues to collaborate (optional)
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email address</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="invite-email"
                        type="email"
                        placeholder="colleague@company.com"
                        value={currentEmail}
                        onChange={(e) => {
                          setCurrentEmail(e.target.value);
                          setEmailError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddEmail();
                          }
                        }}
                        className="pl-10"
                      />
                    </div>
                    <Button type="button" variant="outline" size="icon" onClick={handleAddEmail}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {emailError && (
                    <p className="text-sm text-destructive">{emailError}</p>
                  )}
                </div>

                {inviteEmails.length > 0 && (
                  <div className="space-y-2">
                    <Label>Invitations to send</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {inviteEmails.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{email}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemoveEmail(email)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {inviteEmails.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No invitations added yet</p>
                    <p className="text-xs">You can always invite team members later</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            
            {step < 4 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 gap-2 ai-glow"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleComplete}
                disabled={isLoading}
                className="flex-1 gap-2 ai-glow"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {inviteEmails.length > 0 ? "Send Invites & Get Started" : "Get Started"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Skip option */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={async () => {
                await supabase
                  .from("profiles")
                  .update({ onboarding_completed: true })
                  .eq("id", user?.id);
                navigate("/app");
              }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}